import { AnimatePresence, motion } from "framer-motion";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { OptionTypeResponse } from "../../../codegen-api";
import { LAYER_COLORS } from "../../../constants/design/colors";
import { longhandTimeLeftToExpiry, nanosToSeconds } from "../../../utils/date";
import { Spinner } from "../../shared/Spinner";
import {
  Row as TableRow,
  StrikeHeader,
} from "../../shared/Table/style";
import TooltipExplanation from "../../shared/Tooltip";
import { Cell, SideTableWrapper } from "../style";
import { IHeaderColumn, useOptionsChainTableHeaders } from "./headers";
import {
  DateHeader,
  DateHeaderWrapper,
  OptionsTableHeaderWrapper,
  OptionTypeArrow,
} from "./style";
import { ITableHeaderProps } from "./types";

// ====================
//    TABLE CONTENT
// ====================

export function TableHeader({ strikeWidth, expiry, mode }: ITableHeaderProps) {
  const sideTableWidth: string =
    mode === "both" ? `calc((100% - ${strikeWidth}px)/2)` : "100%";

  const { t } = useTranslation("app", {
    keyPrefix: "OptionsChain.OptionsTable.OptionsTable",
  });
  const { t: translateAll } = useTranslation();
  const { t: tooltip } = useTranslation("tooltips");
  const { bidHeaders, headers } = useOptionsChainTableHeaders();
  const [displayText, setDisplayText] = useState<string>();

  const getTimeLeft = useCallback(
    (expNanos: number) => {
      const exp = moment.unix(nanosToSeconds(expNanos));
      const duration = moment.duration(exp.diff(new Date()));

      if (duration.days() < 1) {
        return (
          longhandTimeLeftToExpiry(translateAll, nanosToSeconds(expNanos)) ||
          "Expired"
        );
      }
      return `${exp.diff(new Date(), "days")} ${
        exp.diff(new Date(), "days") > 1 ? t("days") : t("day")
      }`;
    },
    [t, translateAll]
  );

  // Updates time left every 5 seconds
  useEffect(() => {
    if (expiry) {
      const timeLeft = getTimeLeft(expiry);
      setDisplayText(timeLeft);

      const timer = setInterval(() => {
        setDisplayText(() => getTimeLeft(expiry));
      }, 5000);
      return () => {
        clearInterval(timer);
      };
    }
    return () => {};
  }, [expiry, getTimeLeft]);

  return (
    <OptionsTableHeaderWrapper>
      <DateHeaderWrapper>
        {mode !== "both" && (
          <StrikeHeader
            $width={strikeWidth}
            style={{
              backgroundColor: LAYER_COLORS.highlightLight,
            }}
          >
            <AnimatePresence>
              <motion.span
                key={displayText}
                transition={{
                  duration: 1,
                  ease: "easeInOut",
                }}
                initial={{
                  opacity: 0,
                }}
                animate={{
                  opacity: 1,
                }}
                exit={{
                  opacity: 0,
                }}
              >
                {displayText || <Spinner />}
              </motion.span>
            </AnimatePresence>
          </StrikeHeader>
        )}
        {(mode === "both" || mode === "call") && (
          <DateHeader $width={sideTableWidth}>
            <span>
              {t("calls")} <OptionTypeArrow $type={OptionTypeResponse.Call} />
            </span>
          </DateHeader>
        )}
        {mode === "both" && (
          <StrikeHeader
            $width={strikeWidth}
            style={{
              backgroundColor: LAYER_COLORS.highlightLight,
            }}
          >
            <AnimatePresence>
              <motion.span
                key={displayText}
                transition={{
                  duration: 1,
                  ease: "easeInOut",
                }}
                initial={{
                  opacity: 0,
                }}
                animate={{
                  opacity: 1,
                }}
                exit={{
                  opacity: 0,
                }}
              >
                {displayText || <Spinner />}
              </motion.span>
            </AnimatePresence>
          </StrikeHeader>
        )}
        {(mode === "both" || mode === "put") && (
          <DateHeader $width={sideTableWidth}>
            <span>
              {t("puts")} <OptionTypeArrow $type={OptionTypeResponse.Put} />
            </span>
          </DateHeader>
        )}
      </DateHeaderWrapper>
      <TableRow>
        {mode === "both" ? (
          <>
            <SideTableWrapper $width={sideTableWidth}>
              {Object.values(bidHeaders).map((header) => (
                <TooltipExplanation
                  title={header.label}
                  explanation={header.explanation}
                  key={header.label}
                  renderContent={({ ref, ...triggerHandler }) => (
                    <Cell ref={ref} align={header.align} {...triggerHandler}>
                      {header.label}
                    </Cell>
                  )}
                />
              ))}
              {Object.values(headers).map((header) => (
                <TooltipExplanation
                  title={header.label}
                  explanation={header.explanation}
                  key={header.label}
                  renderContent={({ ref, ...triggerHandler }) => (
                    <Cell ref={ref} align={header.align} {...triggerHandler}>
                      {header.label}
                    </Cell>
                  )}
                />
              ))}
            </SideTableWrapper>
            <StrikeHeader $width={strikeWidth}>
              <TooltipExplanation
                title={t("strike")}
                explanation={tooltip("strike")}
                renderContent={({ ref, ...triggerHandler }) => (
                  <span ref={ref} {...triggerHandler}>
                    {t("strike")}
                  </span>
                )}
              />
            </StrikeHeader>
            <SideTableWrapper $width={sideTableWidth}>
              {Object.values(headers)
                .reverse()
                .map((header) => (
                  <TooltipExplanation
                    title={header.label}
                    explanation={header.explanation}
                    key={header.label}
                    renderContent={({ ref, ...triggerHandler }) => (
                      <Cell ref={ref} align={header.align} {...triggerHandler}>
                        {header.label}
                      </Cell>
                    )}
                  />
                ))}
              {Object.values(bidHeaders).map((header) => (
                <TooltipExplanation
                  title={header.label}
                  explanation={header.explanation}
                  key={header.label}
                  renderContent={({ ref, ...triggerHandler }) => (
                    <Cell ref={ref} align={header.align} {...triggerHandler}>
                      {header.label}
                    </Cell>
                  )}
                />
              ))}
            </SideTableWrapper>
          </>
        ) : (
          <>
            <TooltipExplanation
              title={t("strike")}
              explanation={tooltip("strike")}
              renderContent={({ ref, ...triggerHandler }) => (
                <StrikeHeader $width={strikeWidth} ref={ref} {...triggerHandler}>
                  <span>{t("strike")}</span>
                </StrikeHeader>
              )}
            />
            <SideTableWrapper $width={sideTableWidth}>
              {/* Position */}
              {headers.position && (
                <TooltipExplanation
                  key={headers.position.label}
                  title={headers.position.label}
                  explanation={headers.position.explanation}
                  renderContent={({ ref, ...triggerHandler }) => (
                    <Cell
                      align={headers.position!.align}
                      ref={ref}
                      {...triggerHandler}
                    >
                      {headers.position!.label}
                    </Cell>
                  )}
                />
              )}

              {Object.values(bidHeaders).map((header) => (
                <TooltipExplanation
                  key={header.label}
                  title={header.label}
                  explanation={header.explanation}
                  renderContent={({ ref, ...triggerHandler }) => (
                    <Cell align={header.align} ref={ref} {...triggerHandler}>
                      {header.label}
                    </Cell>
                  )}
                />
              ))}
              {Object.keys(headers)
                .filter((v) => v !== "position")
                .map((key) => {
                  const header: IHeaderColumn =
                    headers[key as keyof typeof headers]!;
                  return (
                    <TooltipExplanation
                      key={header.label}
                      title={header.label}
                      explanation={header.explanation}
                      renderContent={({ ref, ...triggerHandler }) => (
                        <Cell
                          align={header.align}
                          ref={ref}
                          {...triggerHandler}
                        >
                          {header.label}
                        </Cell>
                      )}
                    />
                  );
                })}
            </SideTableWrapper>
          </>
        )}
      </TableRow>
    </OptionsTableHeaderWrapper>
  );
}
